import { useState } from "react";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { authApi, apiError } from "@/lib/api";
import { useLang } from "@/i18n";

export const AuthDialog = ({ open, reason, onClose, onAuthed }) => {
  const { t } = useLang();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const isLogin = mode === "login";

  const submit = async (e) => {
    e.preventDefault();
    if (!isLogin && form.password.length < 8) { setError(t("La password deve contenere almeno 8 caratteri.")); return; }
    setBusy(true); setError("");
    try {
      const u = isLogin ? await authApi.login({ email: form.email, password: form.password }) : await authApi.register(form);
      setForm({ name: "", email: "", password: "" });
      onAuthed(u);
    } catch (err) {
      setError(t(apiError(err)));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="auth-dialog" className="rounded-3xl border-emerald-100 p-8 sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{mode === "forgot" ? t("Reimposta password") : isLogin ? t("Accedi a TripSaver") : t("Crea il tuo account")}</DialogTitle>
          <DialogDescription>{mode === "forgot" ? t("Ti invieremo un link per reimpostare la password.") : reason || t("Salva i tuoi viaggi e ritrovali nel tuo profilo.")}</DialogDescription>
        </DialogHeader>
        {mode === "forgot" ? <ForgotForm initialEmail={form.email} onBack={() => setMode("login")} /> : (<>
        <div className="mt-2 grid grid-cols-2 rounded-full bg-slate-100 p-1 text-sm font-semibold">
          {[["login", "Accedi"], ["register", "Registrati"]].map(([m, l]) => (
            <button key={m} type="button" data-testid={`auth-tab-${m}`} onClick={() => { setMode(m); setError(""); }}
              className={`rounded-full py-2 transition-colors ${mode === m ? "bg-white text-emerald-800 shadow-sm" : "text-gray-500"}`}>{t(l)}</button>
          ))}
        </div>
        <form onSubmit={submit} className="mt-4 space-y-3">
          {!isLogin && <Input data-testid="auth-name-input" placeholder={t("Nome")} value={form.name} onChange={set("name")} required className="h-12 rounded-xl" />}
          <Input data-testid="auth-email-input" type="email" placeholder="Email" value={form.email} onChange={set("email")} required className="h-12 rounded-xl" />
          <Input data-testid="auth-password-input" type="password" placeholder={isLogin ? t("Password") : t("Password (min. 8 caratteri)")} value={form.password} onChange={set("password")} required className="h-12 rounded-xl" />
          {error && <p data-testid="auth-error" className="rounded-xl bg-red-50 px-4 py-2 text-sm text-red-700">{error}</p>}
          <button type="submit" data-testid="auth-submit-button" disabled={busy}
            className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-emerald-600 py-3 font-semibold text-white transition-colors hover:bg-emerald-700 disabled:opacity-60">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}{isLogin ? t("Accedi") : t("Registrati")}
          </button>
          {isLogin && (
            <button type="button" data-testid="auth-forgot-link" onClick={() => { setMode("forgot"); setError(""); }}
              className="w-full text-center text-sm font-medium text-emerald-700 hover:underline">{t("Password dimenticata?")}</button>
          )}
        </form>
        </>)}
      </DialogContent>
    </Dialog>
  );
};

const ForgotForm = ({ initialEmail, onBack }) => {
  const { t, lang } = useLang();
  const [email, setEmail] = useState(initialEmail || "");
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setState("busy"); setError("");
    try { await authApi.forgot(email, lang); setState("sent"); }
    catch (err) { setError(t(apiError(err))); setState("idle"); }
  };
  if (state === "sent") {
    return (
      <div className="mt-2 space-y-4">
        <p data-testid="forgot-sent-message" className="rounded-xl bg-emerald-50 px-4 py-3 text-sm text-emerald-800">{t("Se l'email è registrata, riceverai a breve un link per reimpostare la password.")}</p>
        <button type="button" data-testid="forgot-back-button" onClick={onBack} className="w-full text-sm font-medium text-emerald-700 hover:underline">{t("Torna all'accesso")}</button>
      </div>
    );
  }
  return (
    <form onSubmit={submit} className="mt-2 space-y-3">
      <Input data-testid="forgot-email-input" type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} required className="h-12 rounded-xl" />
      {error && <p data-testid="forgot-error" className="rounded-xl bg-red-50 px-4 py-2 text-sm text-red-700">{error}</p>}
      <button type="submit" data-testid="forgot-submit-button" disabled={state === "busy"}
        className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-emerald-600 py-3 font-semibold text-white hover:bg-emerald-700 disabled:opacity-60">
        {state === "busy" && <Loader2 className="h-4 w-4 animate-spin" />}{t("Inviami il link")}
      </button>
      <button type="button" data-testid="forgot-back-button" onClick={onBack} className="w-full text-sm font-medium text-emerald-700 hover:underline">{t("Torna all'accesso")}</button>
    </form>
  );
};
